// 주문 상태 머신 정의.
// 작업계획서 4.7 + docs/data-model.md 4장의 규칙을 코드로 표현한다.
// 서버에서 상태 전환 시 본 모듈의 canTransition을 통해 항상 검증한다 (클라이언트 신뢰 금지).

export const ORDER_STATUS = {
  RECEIVED: "received",
  ACCEPTED: "accepted",
  REJECTED: "rejected",
  PREPARING: "preparing",
  READY: "ready",
  COMPLETED: "completed",
  CANCELED: "canceled",
} as const;

export type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS];

// 상태 전환 행렬.
// 키는 현재 상태, 값은 허용되는 다음 상태 집합이다.
// 운영 흐름을 단순화해 PREPARING(준비중) 단계를 제거했다: 신규 -> 수락 -> 준비완료 -> 완료.
//  - PREPARING 은 enum/DB CHECK 호환을 위해 남겨두되, 어디서도 전환 대상이 되지 않아 도달 불가능하다(레거시 데이터 안전용).
// CANCELED 는 "취소" 경로다. 진행 중(신규/수락/준비완료) 어느 단계에서도 구조적으로 허용한다.
//  - "누가 언제 취소할 수 있는가"는 라우트/역할 계층에서 강제한다(표는 구조적 가능성만 정의):
//    · 손님 직접 취소: 수락 전(RECEIVED) 에서만 (orders.cancelOrderByUser 가 RECEIVED 로 제한).
//    · 관리자 취소: 진행 중 어느 단계든 (개선 - 관리자 주문 화면에서 진행 도중 취소 허용).
// 운영 정책 변화 시 본 표를 우선 갱신하고 테스트로 회귀를 잡는다.
const TRANSITIONS: Record<OrderStatus, ReadonlyArray<OrderStatus>> = {
  [ORDER_STATUS.RECEIVED]: [ORDER_STATUS.ACCEPTED, ORDER_STATUS.REJECTED, ORDER_STATUS.CANCELED],
  [ORDER_STATUS.ACCEPTED]: [ORDER_STATUS.READY, ORDER_STATUS.CANCELED],
  [ORDER_STATUS.PREPARING]: [ORDER_STATUS.READY, ORDER_STATUS.CANCELED],
  [ORDER_STATUS.READY]: [ORDER_STATUS.COMPLETED, ORDER_STATUS.CANCELED],
  [ORDER_STATUS.COMPLETED]: [],
  [ORDER_STATUS.REJECTED]: [],
  [ORDER_STATUS.CANCELED]: [],
};

// 종결 상태 여부. 적립금 확정/통계 집계 시 사용.
export function isTerminalOrderStatus(status: OrderStatus): boolean {
  return TRANSITIONS[status].length === 0;
}

// 상태 전환 가능 여부.
// 서버 도메인 로직에서 호출하며, 호출자는 false 반환 시 CONFLICT 에러로 응답한다.
export function canTransitionOrderStatus(from: OrderStatus, to: OrderStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

// 현재 상태에서 전환 가능한 다음 상태 목록.
// 관리자 콘솔이 상태 변경 버튼을 렌더링할 때 사용한다 (전환표와 UI 를 단일 출처로 유지).
export function allowedNextOrderStatuses(from: OrderStatus): ReadonlyArray<OrderStatus> {
  return TRANSITIONS[from];
}

// 사용자(손님) UI에 노출하는 한글 라벨.
// 라벨 설계 의도:
//  - RECEIVED 는 "주문" (손님이 주문을 넣은 직후 상태)
//  - ACCEPTED 는 "접수" (가게가 주문을 접수했다는 의미)
//  - REJECTED 는 "취소" 로 표기 (손님 입장에서 "거절" 표현이 불쾌감을 줄 수 있음)
//  - CANCELED(손님 취소) 와 REJECTED 는 손님 화면에서 동일하게 "취소" 로 보인다.
//    내부 enum 은 구분되어 있으므로 관리자 콘솔/통계에서는 따로 구분해 표시한다 (관리자용 라벨 맵은 후속 작업에서 추가).
export const ORDER_STATUS_LABEL_KO: Record<OrderStatus, string> = {
  [ORDER_STATUS.RECEIVED]: "주문",
  [ORDER_STATUS.ACCEPTED]: "접수",
  [ORDER_STATUS.PREPARING]: "준비중",
  [ORDER_STATUS.READY]: "준비완료",
  [ORDER_STATUS.COMPLETED]: "완료",
  [ORDER_STATUS.REJECTED]: "취소",
  [ORDER_STATUS.CANCELED]: "취소",
};

// 관리자 콘솔용 라벨.
// 손님 화면과 달리 거절(REJECTED) 과 사용자취소(CANCELED) 를 구분해 운영자가 원인을 파악할 수 있게 한다.
export const ORDER_STATUS_ADMIN_LABEL_KO: Record<OrderStatus, string> = {
  [ORDER_STATUS.RECEIVED]: "신규",
  [ORDER_STATUS.ACCEPTED]: "수락",
  [ORDER_STATUS.PREPARING]: "준비중",
  [ORDER_STATUS.READY]: "준비완료",
  [ORDER_STATUS.COMPLETED]: "완료",
  [ORDER_STATUS.REJECTED]: "거절",
  [ORDER_STATUS.CANCELED]: "취소",
};
